import React from 'react';
import Sidebar, { MobileTopBar } from './Sidebar';
import Footer from './Footer';
import PresentationBanner from './PresentationBanner';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ShieldWarning, Info } from '@phosphor-icons/react';
import AddToHomeScreenTip from '@/components/AddToHomeScreenTip';

function ImpersonationBanner() {
    const { user, endImpersonation } = useAuth();
    const navigate = useNavigate();
    if (!user?.impersonating_by) return null;
    const end = async () => {
        await endImpersonation();
        navigate('/admin');
    };
    return (
        <div
            data-testid="app-impersonation-banner"
            className="w-full flex items-center justify-center gap-3 bg-red-600 text-white text-xs px-4 py-2 font-medium"
        >
            <ShieldWarning size={16} weight="fill" />
            <span>
                Impersonating <span className="font-semibold">{user.name}</span>
                <span className="opacity-80"> ({user.email})</span> — everything you do here is recorded.
            </span>
            <button
                data-testid="app-end-impersonation"
                onClick={end}
                className="ml-2 rounded-full bg-white text-red-700 px-3 py-0.5 text-[11px] font-semibold uppercase tracking-widest hover:bg-red-50"
            >
                Return to admin
            </button>
        </div>
    );
}

function DemoAccountBanner() {
    const { user } = useAuth();
    if (!user?.is_demo && !user?.is_demo_admin) return null;
    const isAdminDemo = !!user?.is_demo_admin;
    return (
        <div
            data-testid={isAdminDemo ? 'demo-admin-banner' : 'demo-account-banner'}
            className={`w-full flex items-center justify-center gap-2 text-[11px] py-1.5 px-4 border-b ${
                isAdminDemo ? 'bg-indigo-100 text-indigo-900 border-indigo-200' : 'bg-amber-100 text-amber-900 border-amber-200'
            }`}
        >
            <Info size={12} weight="fill" />
            <span>{isAdminDemo ? 'Demo admin — view only.' : 'Demo data — this family is fictional.'}</span>
        </div>
    );
}

export default function CoordinatorShell() {
    return (
        <div className="min-h-screen flex flex-col bg-background">
            <PresentationBanner />
            <ImpersonationBanner />
            <DemoAccountBanner />
            <div className="flex flex-1 min-h-0">
                <Sidebar />
                <main className="flex-1 min-w-0 overflow-y-auto flex flex-col">
                    <MobileTopBar />
                    <div className="flex-1"><Outlet /></div>
                    <Footer />
                </main>
            </div>
            <AddToHomeScreenTip />
        </div>
    );
}
